import * as loader from '../loader-support.mjs';
import * as runtime from '../runtime-support.mjs';


/** 
 * Logic that supports the loading of scripts into the browser client for post pages.
 */

/**
 * Bootstrap loading of scripts that are downstream of the initial bootstrap script. 
 * This activity is complicated by the need to polyfill for older browsers (i.e., Internet 
 * Explorer v11 and above). Dynamically/selectively appending the polyfill scripts without 
 * pulling in the other scripts in the same manner is prone to introducing timing issues. 
 * As such, implement a series of cascading onloads, with each downstream script pulled-in via 
 * the onload of the preceeding upstream script.
 */
export const loadDownstreamScripts = function() {
	// Don't bother if the browser is unsupported.
	if (!runtime.browserUnsupported) {

	    let scriptSrcArr = [];

		if (!runtime.browserFullFeatured) {
			scriptSrcArr.push( polyfillFetchApi() );
		}

	    scriptSrcArr.push('/assets/scripts/scripting-for-posts-part-2.{{ global.cacheBuster }}.js');

	    if (!runtime.browserFullFeatured) {
	    	scriptSrcArr.push( polyfillForTooltips() );
	    }

	    scriptSrcArr.push({ 
            // Pair a stylesheet with the script.
            script: '/assets/scripts/scripting-for-posts-part-3.{{ global.cacheBuster }}.js', 
            style: '/assets/styling/styling-for-posts-part-3.{{ global.cacheBuster }}.css'
        });

	    loader.appendScriptsUsingCascadingLoads(scriptSrcArr);
	}
};

/**
 * Build and return a URL (for a CDN) that can be accessed to return scripts to polyfill the Fetch 
 * Api, making it available in browsers that are supported, but not full-featured (meaning 
 * IE v11 and Microsoft Edge).
 */
const polyfillFetchApi = function() {
	let result = null;

	let polyfillFeaturesArr = [];
    /*
     * Promise polyfill supports IE v8+. More info: 
     *	https://github.com/taylorhakes/promise-polyfill
     */
    polyfillFeaturesArr.push('npm/promise-polyfill@8/dist/polyfill.min.js');

    /*
     * Fetch polyfill supports IE v10+. More info: https://github.com/github/fetch
     */
    polyfillFeaturesArr.push('npm/whatwg-fetch@3.5.0/dist/fetch.umd.min.js');

    /*
     * FormData polyfill supports IE v10+. More info: 
     *	https://github.com/jimmywarting/FormData
     *
     * Use npm/formdata-polyfill@3.0.20/FormData.js for non-minified version.
     */
	polyfillFeaturesArr.push('npm/formdata-polyfill@3.0.20/formdata.min.js');

    /*
     * URLSearchParams polyfill supports IE v8+. More info: 
     *	https://github.com/jerrybendy/url-search-params-polyfill
     */
	polyfillFeaturesArr.push('npm/url-search-params-polyfill@8.1.0/index.min.js');

    if (polyfillFeaturesArr.length > 0) {
        const polyfillFeatures = polyfillFeaturesArr.join();

        console.log('Polyfilling %s', polyfillFeatures);
        /*
         * When polyfilling for the Fetch API, I had more success using the combination of 
         * libraries provided by jsdelivr.com than Polyfill.io.
         */
        result = 'https://cdn.jsdelivr.net/combine/' + polyfillFeatures;
    }

    return result;
};

/**
 * Build and return a URL (for a CDN) that can be accessed to return scripts to polyfill 
 * functionality needed by Tippy. More info: https://atomiks.github.io/tippyjs/v6/browser-support/
 * Our usage of tooltips are important, but not critical. The user can still read the site content 
 * without them. Tooltips are (currently) only used to help guide the user when submitting forms. 
 */
const polyfillForTooltips = function(scriptSrcArr) {
    let result = null;

    let polyfillFeaturesArr = [];
    /*
     * Mimic the feature detection tests used by the individual polyfills, themselves, to 
     * selectively request the polyfill features.
     */
    if ( !('assign' in Object) ) {
        polyfillFeaturesArr.push('Object.assign');
    }
    if ( !('find' in Array.prototype) ) {
        polyfillFeaturesArr.push('Array.prototype.find');
    }
    
    if (polyfillFeaturesArr.length > 0) {
        const polyfillFeatures = polyfillFeaturesArr.join();

        console.log('Polyfilling %s', polyfillFeatures);
        /*
         * Set the flag to include feature detection, as polyfills are pulled-in more than once. 
         * More info on the Polyfill.io API: https://polyfill.io/v3/api/
    	 * Consider using core-js instead of Polyfill.io - https://github.com/zloirock/core-js
         */
        result = 'https://polyfill.io/v3/polyfill.js?features=' + polyfillFeatures + '&flags=gated';
    }

    return result;
};
